import styled from 'styled-components';
import {
  BodyText,
  Checkbox,
  Dropdown,
  DropdownValue,
  FlexColumn,
  FlexRow,
  SvgIcon,
  Textarea
} from '@make-software/cspr-design';
import React, { useEffect, useState } from 'react';
import {
  ErrorText,
  ErrorTextContainer,
  FormContainer,
  StyledConfirmButton,
  StyledDismissButton,
  StyledInput,
} from 'app/components/common/common-styles';
import { TradeType } from 'app/console-api/offers/offers';
import { createOffer } from 'app/console-api';
import { EntityType, getPluginsList } from 'app/console-api/plugins/plugins';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { offerValidationSchema } from 'app/components/management/apps/offers/common/offer-validation-schema';
import { Link, useNavigate } from 'react-router-dom';
import { HttpError } from 'app/components/utils/http-error';
import { AUTH_TOKEN, customErrorMessage, statusCode } from 'app/constants';
import { SIGN_IN_PATH, APPS_CREATE_PATH } from 'app/route/paths';
import SuccessEntityWindow from 'app/components/common/success-entity-modal/success-entity-modal';
import CreateSuccessIcon from 'assets/logos/success-entity.svg';
import { ErrorResult } from 'app/organizations-api/types';
import { ErrorWindow } from 'app/components/common/error-window/error-window';
import NotFoundIcon from 'assets/logos/no-found.svg';
import ErrorEntityWindow from 'app/components/common/error-entity-modal/error-entity-modal';
import { pluginFormControlMap } from 'app/components/management/common/plugins/form-control-map';
import { useTabsCtx } from 'app/components/management/apps/tabs-page-layout';
import { DealTradeTypeSelect } from 'app/components/common/deal-trade-type/deal-trade-type-select';

const StyledButtonRow = styled(FlexRow)(({ theme }) =>
  theme.withMedia({
    margin: '40px 0 0 0'
  })
);

export interface PluginDropdownValue extends DropdownValue {
  id: number;
}

export const OfferCreateForm: React.FC = () => {
  const { appID } = useTabsCtx();
  const [pluginList, setPluginList] = useState<PluginDropdownValue[]>([]);
  const [openSuccessModal, setOpenSuccessModal] = useState<boolean>(false);
  const [unexpectedError, setUnexpectedError] = useState<ErrorResult | null>(null);
  const [openConflictErrorModal, setOpenConflictErrorModal] = useState<boolean>(false);
  const [conflictError, setConflictError] = useState<string>('');
  const [formData, setFormData] = useState({
    plugin: { value: '', label: '', id: 0 },
    name: '',
    description: '',
    dealType: '' as TradeType,
    pluginMetadata: {},
    customMetadata: '',
    isPublic: true,
    isPublicStats: true,
    minQuota: 0
  });

  const SelectedFormControls = pluginFormControlMap[formData.plugin.id];

  const navigate = useNavigate();

  const {
    handleSubmit,
    setValue,
    clearErrors,
    formState: { errors }
  } = useForm({
    resolver: yupResolver(
      SelectedFormControls
        ? offerValidationSchema.concat(SelectedFormControls.validationSchema)
        : offerValidationSchema
    ),
    defaultValues: formData
  });

  useEffect(() => {
    getPluginsList(EntityType.Deal)
      .then((plugins) => {
        const res = plugins.map((plugin) => {
          return {
            label: plugin.name,
            value: plugin.id.toString(),
            id: plugin.id
          };
        });
        setPluginList(res || []);
      })
      .catch((error: any) => {
        if (error instanceof HttpError) {
          if (error.statusCode === statusCode.authentication_error) {
            window.location.href = SIGN_IN_PATH;
            return;
          }
        }
        console.error('Failed to fetch plugins:', error);
      });
  }, [appID]);

  const handleInputChange = <K extends keyof typeof formData>(
    key: K,
    value: (typeof formData)[K]
  ) => {
    setFormData((prev) => ({
      ...prev,
      [key]: value
    }));
    // @ts-ignore
    setValue(key, value);

    // @ts-ignore
    clearErrors(key);
  };

  const handleSubmitData = () => {
    createOffer(appID, {
      name: formData.name,
      description: formData.description,
      tradeType: formData.dealType,
      pluginMetadata: formData.pluginMetadata,
      customMetadata: formData.customMetadata ? JSON.parse(formData.customMetadata) : null,
      pluginID: formData.plugin.id,
      isPublic: formData.isPublic,
      isPublicStats: formData.isPublicStats,
      minQuota: formData.minQuota
    })
      .then(() => {
        setOpenSuccessModal(true);
      })
      .catch((error: any) => {
        if (error instanceof HttpError) {
          switch (error.statusCode) {
            case statusCode.validation_error:
              setUnexpectedError({
                code: error.statusCode.toString(),
                message: error.message
              });
              break;
            case statusCode.conflict_error:
              setConflictError(error.info?.error.description || error.message);
              setOpenConflictErrorModal(true);
              break;
            case statusCode.authentication_error:
              localStorage.removeItem(AUTH_TOKEN);
              window.location.href = SIGN_IN_PATH;
              return;
            case statusCode.unexpected_error:
              setUnexpectedError(customErrorMessage);
              break;
            default:
              setUnexpectedError(customErrorMessage);
              break;
          }
        } else {
          setUnexpectedError(customErrorMessage);
        }

        console.error('Failed to create offer:', error);
      });
  };

  const handleCloseConflictErrorModal = () => {
    setOpenConflictErrorModal(false);
  };

  const sentData = () => {
    handleSubmit(handleSubmitData)();
  };

  const handleOnDismiss = () => {
    clearErrors();
    navigate(-1);
  };

  const handleCloseSuccessModal = () => {
    setOpenSuccessModal(false);
    navigate(-1);
  };

  if (unexpectedError) {
    return (
      <ErrorWindow
        withModal
        title={unexpectedError.code}
        information={unexpectedError.message}
        icon={<SvgIcon src={NotFoundIcon} width={219} height={125} />}
      />
    );
  }

  return (
    <FormContainer>
      <SuccessEntityWindow
        dismissLabel={'Close'}
        onDismiss={handleCloseSuccessModal}
        isOpen={openSuccessModal}
        icon={<SvgIcon src={CreateSuccessIcon} width={195} height={120} />}
        title="Offer has been created"
        information="The offer was successfully created."
      />
      <ErrorEntityWindow
        dismissLabel={'Close'}
        onDismiss={handleCloseConflictErrorModal}
        isOpen={openConflictErrorModal}
        icon={<SvgIcon src={NotFoundIcon} width={195} height={120} />}
        title={conflictError}
        information={''}
      />
      <FlexColumn gap={10}>
        <FlexColumn gap={4}>
          <BodyText size={1}>Deal Type</BodyText>
         <DealTradeTypeSelect
           dealType={formData.dealType}
           onChange={(o: any) => handleInputChange('dealType', o.key)}
         />
        </FlexColumn>
        {errors?.dealType?.message && (
          <ErrorTextContainer justify={'flex-start'}>
            <ErrorText size={3}>{errors?.dealType?.message}</ErrorText>
          </ErrorTextContainer>
        )}
        <StyledInput
          required={true}
          label={<BodyText size={1}>Name</BodyText>}
          placeholder={'Offer name here'}
          value={formData.name}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            handleInputChange('name', e.target.value);
          }}
        />
        {errors?.name?.message && (
          <ErrorTextContainer justify={'flex-start'}>
            <ErrorText size={3}>{errors?.name?.message}</ErrorText>
          </ErrorTextContainer>
        )}
        <Textarea
          label={<BodyText size={1}>Description</BodyText>}
          placeholder={'Offer description here'}
          value={formData.description}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            handleInputChange('description', e.target.value);
          }}
        />
        {errors?.description?.message && (
          <ErrorTextContainer justify={'flex-start'}>
            <ErrorText size={3}>{errors?.description?.message}</ErrorText>
          </ErrorTextContainer>
        )}
        {(["buy", "exchange"].includes(formData.dealType)) && (
          <>
            <Dropdown
              label={'Type'}
              value={formData.plugin}
              placeholder={'Select offer type'}
              items={pluginList}
              onSelect={(e) => handleInputChange('plugin', e as unknown as PluginDropdownValue)}
            />
            {errors?.plugin?.message && (
              <ErrorTextContainer justify={'flex-start'}>
                <ErrorText size={3}>{errors?.plugin?.message}</ErrorText>
              </ErrorTextContainer>
            )}
            {SelectedFormControls ? (
              <SelectedFormControls.formComponent
                metadata={null}
                errors={errors}
                onChange={(value: any) => {
                  handleInputChange('pluginMetadata', value);
                }}
              />
            ) : null}
            <Textarea
              label={<BodyText size={1}>Custom metadata</BodyText>}
              placeholder={'Custom JSON'}
              value={formData.customMetadata?.toString() || undefined}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                handleInputChange('customMetadata', e.target.value);
              }}
            />
            {errors?.customMetadata?.message && (
              <ErrorTextContainer justify={'flex-start'}>
                <ErrorText size={3}>{errors?.customMetadata?.message}</ErrorText>
              </ErrorTextContainer>
            )}
          </>
        )}
        <StyledInput
          label={<BodyText size={1}>Min daily quota</BodyText>}
          value={formData.minQuota > 0 ? formData.minQuota : ''}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            const parsed = parseInt(e.target.value);
            handleInputChange('minQuota', isNaN(parsed) ? 0 : parsed);
          }}
        />
        {errors?.minQuota?.message && (
          <ErrorTextContainer justify={'flex-start'}>
            <ErrorText size={3}>{errors?.minQuota?.message}</ErrorText>
          </ErrorTextContainer>
        )}
        <Checkbox
          label={<BodyText size={1}>Publish to Catalog</BodyText>}
          checked={formData.isPublic}
          onChange={() => {
            handleInputChange('isPublic', !formData.isPublic);
          }}
        />
        <Checkbox
          label={<BodyText size={1}>Show Statistics</BodyText>}
          checked={formData.isPublicStats}
          onChange={() => {
            handleInputChange('isPublicStats', !formData.isPublicStats);
          }}
        />
        <StyledButtonRow gap={10}>
          <StyledDismissButton color={'secondaryBlue'} onClick={handleOnDismiss}>
            Cancel
          </StyledDismissButton>
          <StyledConfirmButton color="primaryBlue" onClick={sentData}>
            Create
          </StyledConfirmButton>
        </StyledButtonRow>
      </FlexColumn>
    </FormContainer>
  );
};
